import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-500aa509"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 0,
  class: "label"
}
const _hoisted_2 = { class: "dropdown-container" }

import { ref, PropType, computed } from "vue";
import { IDDescriptionPair } from "@/types/generic/IDDescriptionPair";
import DxDropDownBox from "devextreme-vue/drop-down-box";
import DxTreeView from "devextreme-vue/tree-view";
import { FieldSize } from "@/enums/system/FieldSize";


export default /*#__PURE__*/_defineComponent({
  __name: 'multi-select-filter',
  props: {
  /**
   * The selected values for the component.
   * Used with `v-model` to track and update the selected ID-description pairs.
   * @type {IDDescriptionPair[]}
   * @required
   */
  modelValue: {
    type: Object as PropType<IDDescriptionPair[]>,
    required: true,
  },

  /**
   * The available data options that can be selected.
   * Contains a list of ID-description pairs.
   * @type {IDDescriptionPair[]}
   * @required
   */
  dataSource: {
    type: Array as PropType<IDDescriptionPair[]>,
    required: true,
  },

  /**
   * The title or heading associated with the component.
   * Typically displayed as a label or section header.
   * @type {string}
   * @required
   */
  title: {
    type: String,
    required: true,
  },

  /**
   * The width of the field, defined by the `FieldSize` enum.
   * Determines the size of the component within its container.
   * @type {FieldSize}
   * @default FieldSize.Full
   */
  width: {
    type: String as PropType<FieldSize>,
    default: FieldSize.Full,
  },

  /**
   * Determines whether the control is read-only.
   * If `true`, the user cannot modify the selection.
   * @type {boolean}
   * @default false
   */
  readOnly: {
    type: Boolean,
    default: false,
  },
},
  emits: [
  /**
   * Emitted when the `modelValue` is updated, typically triggered when a selection changes.
   * @event update:modelValue
   * @param {IDDescriptionPair} value - The new value to be updated in the model.
   */
  "update:modelValue",
],
  setup(__props, { emit }) {

const props = __props;





// used to store a ref of the treeview when rendered
const treeview = ref();

const filter = computed({
  get: () => props.modelValue,
  set: (value: IDDescriptionPair[]) => emit("update:modelValue", value),
});

/** Formats the selected values so they can be displayed to the end user in the drop down */
const displayValueFormatter = () => {
  return filter.value
    ? filter.value.map((item) => item.description).join(", ")
    : "";
};

/** Handles the treeview item selection change event. This will update the value bound to the drop down */
const onItemSelectionChanged = (e: any) => {
  /* As a user can search within the TreeView, it will only return back the Visible nodes from the TreeView when using most methods
   * To retrieve both visible nodes and nodes that arn't visible, we should use the line below to retrieve ALL nodes from the TreeView regardless of any search filters
   * Otherwise nodes that arn't visible won't display in the filters even though they are still selected. */
  const nodes = e.component.option("items");

  // set the filters to only the selected nodes from all of the nodes that we retrieved
  filter.value = getSelectedNodes(nodes);
};

/** Gets only the selected nodes from all the nodes */
const getSelectedNodes = (nodes: any) => {
  const selectedNodes: IDDescriptionPair[] = [];

  // Loop around each of the nodes and find the selected nodes
  nodes.forEach((node: any) => {
    if (node.selected) {
      // add the selected node to the array of selected nodes
      selectedNodes.push({ id: node.id, description: node.description });
    }
  });

  return selectedNodes;
};

/** Handles when the event triggered when treeview content is displayed for the first time */
const onTreeviewContentReady = (e: any) => {
  treeview.value = e.component;

  // To avoid errors, sanity check treeview has been rendered
  if (!treeview.value) {
    return;
  }

  /* If no items are selected, make sure treeview is initialised with no selected nodes
   * this will prevent unexpected results and user confusion */
  if (e.value === null || filter.value === null || filter.value.length === 0) {
    unselectAllTreeViewItems();
    return;
  }

  // As the datasource passed to the treeview has no 'selected' field, we need to manually select items
  filter.value.forEach((item) => treeview.value.selectItem(item.id));
};

/** Unselects all items within the TreeView component */
const unselectAllTreeViewItems = () => {
  // ensure that the treeview has been rendered before trying to unselect all items
  if (!treeview.value) {
    return;
  }

  treeview.value.unselectAll();
};

/** Handles when the value changed event is triggered on the Drop Down Box */
const onValueChanged = (e: any) => {
  if (e.value === null) {
    // To prevent errors and caching incorrect filters, reset the current value to a blank array
    filter.value = [];

    // For a better user experience, close the drop down when the reset button clicked
    e.component.close();
  }

  // When the value is null or a blank array, it should unselect all the treeview items
  if (e.value?.length === 0) {
    // we should unselect all items in the Tree View, as the filters have been cleared
    unselectAllTreeViewItems();
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (__props.title)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, _toDisplayString(__props.title), 1))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_unref(DxDropDownBox), {
        value: filter.value,
        "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((filter).value = $event)),
        "show-clear-button": true,
        onValueChanged: onValueChanged,
        "data-source": __props.dataSource,
        "value-expr": "id",
        "display-expr": "description",
        placeholder: "Select a value...",
        "display-value-formatter": displayValueFormatter,
        width: props.width,
        readOnly: props.readOnly
      }, {
        content: _withCtx(() => [
          _createVNode(_unref(DxTreeView), {
            "data-source": __props.dataSource,
            "select-by-click": true,
            "select-nodes-recursive": false,
            "data-structure": "plain",
            "key-expr": "id",
            "selection-mode": "multiple",
            "show-check-boxes-mode": "normal",
            "display-expr": "description",
            onItemSelectionChanged: onItemSelectionChanged,
            "search-enabled": true,
            "search-expr": "description",
            onContentReady: onTreeviewContentReady
          }, null, 8, ["data-source"])
        ]),
        _: 1
      }, 8, ["value", "data-source", "width", "readOnly"])
    ])
  ], 64))
}
}

})